<script setup lang="ts">
import { PlanResponse } from '@/planList/domain/PlanResponse';
import { formatHelpers } from '@hfc/ui-core';
import { Detail } from '@/planList/domain/Detail';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { useCarrierInfo } from '@/application/shared/infrastructure/composition/useCarrierInfo';

const { formatPrice } = formatHelpers();
const { getCarrierLogoId } = useCarrierInfo();

const props = defineProps<{
  plan: PlanResponse;
  applicationType: APPLICATION_TYPES;
  ignoredDetails?: Array<string>;
  hidePlanBanner?: boolean;
}>();

const hasDescriptionClass = (detail: Detail): string => detail.description ? 'font-semibold px-10' : 'font-bold text-lg bg-white -ml-8 exclude-highlighter';
const parsedDescription = (description: string): string => {
  return description.replace(/\n/g, '<br/>');
};
const filteredDetails = (details: Array<Detail>): Array<Detail> => {
  if (!props.ignoredDetails || props.ignoredDetails.length === 0) {
    return details;
  }

  return details.filter((detail) => !props.ignoredDetails?.includes(detail.id));
};
</script>

<template>
  <div class="flex flex-col lining-nums" v-highlight="{ multipleCoincidences: true }">
    <div class="flex flex-col">
      <div v-if="!hidePlanBanner">
        <div class="grid items-center md:grid-col md:grid-cols-2 py-6">
          <div class="col flex justify-start  order-2 md:order-none text-left">
            <h2 class="text-2xl font-semibold text-blue-600 exclude-highlighter">{{ plan.name }}</h2>
            <!-- Here we should review if we need twoLineName -->
          </div>
          <div class="col flex justify-center mb-6 md:mb-0 md:justify-end order-1 md:order-none">
            <carrier-logo class="w-56 lg:w-72 px-6 flex items-center" :carrier-id="getCarrierLogoId(plan)" :coverage-type="applicationType" vertical="true" no-link="true" />
          </div>
        </div>
        <div class="flex items-center justify-start font-bold text-4xl text-red animate-[bounce_500ms_ease-out] transition-all duration-300">
          <span class="md:pl-4">{{ formatPrice(plan.rate) }}</span><span class="text-lg mt-2">/month</span>
        </div>
      </div>
      <div class="grid pb-8 text-sm sm:text-md">
        <div v-if="plan.benefit_summary && plan.benefit_summary !== ''" class="flex md:px-4 py-4">
          <p>View summary below or click here for more information <a :href="plan.benefit_summary" target="_blank">Detailed Benefits</a></p>
        </div>
        <div v-for="(detail, i) in filteredDetails(plan?.details)" class="flex even:bg-gray-200 odd:bg-gray-100 ml-8" :key="i">
          <div class="flex flex-1 py-4" :class="hasDescriptionClass(detail)">{{ detail.title }}</div>
          <div v-if="detail.description" class="flex flex-1 items-center justify-end px-10 text-right" v-html="parsedDescription(detail.description)"></div>
        </div>
      </div>
    </div>
  </div>
</template>
